import { useState } from 'react';
import { api, monthLabel, sizeLabel, usd } from '../lib/api';
import { addMonths, firstOfMonth, todayIn } from '@njpos/core';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import NewItemButton from '../components/NewItemButton';

const STATUS: Record<string, string> = { draft: '', sent: 'amber', partial: 'warn', received: 'good', cancelled: 'bad' };

export default function PurchaseOrders() {
  const list = useLoad(() => api<any[]>('/api/purchase-orders'), []);
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Purchase orders</h1><p>Each PO goes to one vendor for one delivery on one invoice, so the RIP counts as a single purchase.</p></div>
        <button className="primary" onClick={() => setCreating(true)}>New PO</button></div>
      <ErrorNote error={list.error} />
      <div className="panel">
        {list.data?.length === 0 ? <div className="empty">No purchase orders yet. Press New PO, or pick deals on the buy list to create them.</div> : (
          <table className="grid">
            <thead><tr><th>PO</th><th>Vendor</th><th>Month</th><th>Status</th><th className="n">Lines</th><th className="n">Expected invoice</th><th className="n">Expected RIP</th></tr></thead>
            <tbody>{list.data?.map((po) => (
              <tr key={po.id} className="clickable" onClick={() => setOpen(po.id)}>
                <td style={{ fontWeight: 600 }}>{po.po_number}</td><td>{po.wholesaler_name}</td><td>{po.sale_month ? monthLabel(po.sale_month) : '—'}</td>
                <td><span className={`chip ${STATUS[po.status]}`}>{po.status}</span></td><td className="n">{po.line_count}</td>
                <td className="n">{usd(po.expected_total_cents)}</td><td className="n">{po.expected_rip_cents ? usd(po.expected_rip_cents) : '—'}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
      {creating && <NewPo onClose={() => setCreating(false)} onDone={(id) => { setCreating(false); list.reload(); setOpen(id); }} />}
      {open && <PoDetail id={open} onClose={() => { setOpen(null); list.reload(); }} />}
    </div>
  );
}

function PoDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const po = useLoad(() => api<any>(`/api/purchase-orders/${id}`), [id]);
  const [err, setErr] = useState<unknown>(null);
  const act = async (what: 'send' | 'cancel') => { try { await api(`/api/purchase-orders/${id}/${what}`, { method: 'POST' }); po.reload(); } catch (e) { setErr(e); } };
  const print = async () => {
    const res = await api<Response>(`/api/purchase-orders/${id}/print`, { raw: true });
    const html = await res.text();
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  };
  const d = po.data;
  return (
    <Modal wide title={d ? `${d.po_number} · ${d.wholesaler_name}` : 'Purchase order'} onClose={onClose} footer={d && <>
      <button onClick={print}>Print or save PDF</button>
      {d.status === 'draft' && <button className="danger" onClick={() => act('cancel')}>Cancel PO</button>}
      {d.status === 'draft' && <button className="primary" onClick={() => act('send')}>Mark as sent</button>}
    </>}>
      <ErrorNote error={err ?? po.error} />
      {d && <>
        <p className="muted">Status <span className={`chip ${STATUS[d.status]}`}>{d.status}</span> · expected invoice {usd(d.expected_total_cents)}{d.expected_rip_cents ? ` · expected RIP ${usd(d.expected_rip_cents)}` : ''}</p>
        <table className="grid">
          <thead><tr><th>Item code</th><th>Product</th><th className="n">Cases</th><th className="n">CPL case price</th><th className="n">Received</th></tr></thead>
          <tbody>{d.lines.map((l: any) => (
            <tr key={l.id}><td className="faint">{l.wholesaler_item_code ?? '—'}</td><td>{l.brand} {l.name} <span className="faint">{sizeLabel(l.size_ml)}</span></td>
              <td className="n">{l.qty_cases}{l.qty_bottles ? ` + ${l.qty_bottles} btl` : ''}</td><td className="n">{usd(l.expected_case_cents)}</td><td className="n">{l.received_bottles} btl</td></tr>
          ))}</tbody>
        </table>
        <p className="faint">When the delivery arrives, photograph the invoice on the Invoices page and pick this PO so quantities and prices are checked against it.</p>
      </>}
    </Modal>
  );
}

interface Line { item: any; cases: string; bottles: string }

// A purchase order written by hand: pick the vendor, add the items and how many cases. Prices come from that vendor's price list for the month.
function NewPo({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const today = todayIn();
  const thisMonth = firstOfMonth(today); const nextMonth = addMonths(thisMonth, 1);
  const vendors = useLoad(() => api<any[]>('/api/wholesalers'), []);
  const [vendor, setVendor] = useState('');
  const [month, setMonth] = useState(thisMonth);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [q, setQ] = useState('');
  const found = useLoad(() => (q.length >= 2 ? api<any[]>(`/api/items?q=${encodeURIComponent(q)}&limit=8`) : Promise.resolve([])), [q]);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const add = (it: any, cases = '1') => setLines((ls) => (ls.some((l) => l.item.id === it.id) ? ls : [...ls, { item: it, cases, bottles: '' }]));
  const set = (i: number, p: Partial<Line>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...p } : l)));
  const num = (v: string) => v.replace(/\D/g, '');
  const valid = lines.filter((l) => Number(l.cases || 0) + Number(l.bottles || 0) > 0);

  // everything this vendor is the usual source for that is at or below its reorder point
  async function addLow() {
    setErr(null);
    try {
      const low = await api<any[]>('/api/items?lowStock=true&limit=500');
      const mine = low.filter((i) => i.preferred_wholesaler_id === vendor && !i.is_bundle);
      if (!mine.length) { setErr(new Error('Nothing from this vendor is at or below its reorder point.')); return; }
      mine.forEach((i) => add(i, String(Math.max(1, Math.ceil(Math.max(i.reorder_point * 2 - i.on_hand, 1) / (i.case_pack || 12))))));
    } catch (e) { setErr(e); }
  }
  async function create() {
    setBusy(true); setErr(null);
    try {
      const po = await api<any>('/api/purchase-orders', { body: { wholesalerId: vendor, saleMonth: month, notes: notes.trim() || undefined, lines: valid.map((l) => ({ itemId: l.item.id, qtyCases: Number(l.cases || 0), qtyBottles: Number(l.bottles || 0) })) } });
      onDone(po.id);
    } catch (e) { setErr(e); setBusy(false); }
  }

  return (
    <Modal wide title="New purchase order" onClose={busy ? () => {} : onClose} footer={<button className="primary" disabled={busy || !vendor || valid.length === 0} onClick={create}>{busy ? 'Creating…' : `Create PO (${valid.length} item${valid.length === 1 ? '' : 's'})`}</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Vendor *<select value={vendor} onChange={(e) => setVendor(e.target.value)}><option value="">Choose…</option>{vendors.data?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</select></label>
        <label>Price list month<select value={month} onChange={(e) => setMonth(e.target.value)}><option value={thisMonth}>{monthLabel(thisMonth)}</option><option value={nextMonth}>{monthLabel(nextMonth)}</option></select></label>
      </div>
      <label>Add an item<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your items by name, brand or barcode" /></label>
      {(found.data ?? []).filter((i) => !lines.some((l) => l.item.id === i.id)).map((i) => (
        <button key={i.id} className="ghost" style={{ display: 'block', width: '100%', textAlign: 'left' }} onClick={() => { add(i); setQ(''); }}>{i.brand} {i.name} · {sizeLabel(i.size_ml)} <span className="faint">({i.on_hand} on hand, {i.case_pack}/case)</span></button>))}
      <div className="row wrap" style={{ gap: 8 }}>
        <NewItemButton onCreated={(i) => add(i)} />
        <button disabled={!vendor} title={vendor ? '' : 'Choose the vendor first'} onClick={addLow}>Add low-stock items from this vendor</button>
      </div>
      {lines.length > 0 && (
        <div className="table-wrap"><table className="grid">
          <thead><tr><th>Item</th><th className="n">On hand</th><th className="n">Cases</th><th className="n">+ Bottles</th><th className="n">Bottles in all</th><th /></tr></thead>
          <tbody>{lines.map((l, i) => (
            <tr key={l.item.id}>
              <td>{l.item.brand} {l.item.name} <span className="faint">{sizeLabel(l.item.size_ml)} · {l.item.case_pack}/case</span></td><td className="n">{l.item.on_hand}</td>
              <td className="n"><input className="n" style={{ width: 64 }} inputMode="numeric" value={l.cases} onChange={(e) => set(i, { cases: num(e.target.value) })} aria-label="Cases" /></td>
              <td className="n"><input className="n" style={{ width: 64 }} inputMode="numeric" value={l.bottles} onChange={(e) => set(i, { bottles: num(e.target.value) })} aria-label="Extra bottles" /></td>
              <td className="n">{Number(l.cases || 0) * (l.item.case_pack || 12) + Number(l.bottles || 0)}</td>
              <td><button className="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))}>Remove</button></td>
            </tr>))}</tbody>
        </table></div>)}
      <label>Note for the vendor (optional)<input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Delivery day, rep name…" /></label>
      <p className="faint">After you create it, the expected invoice and any rebate (RIP) are worked out from this vendor's price list for the month. Then print it or mark it as sent.</p>
    </Modal>
  );
}
